import { useEffect, useState } from "react";
import { X } from "lucide-react";

import {
  TextEditor, ImageEditor, ButtonEditor, StyleTab, AdvancedTab, typeLabel,
} from "@/components/dashboard/inline-editor/RightPanel";
import { ProductsEditor, MenuEditor, ServicesEditor } from "@/components/dashboard/inline-editor/editors/ListEditors";
import { HeaderEditor, FooterEditor } from "@/components/dashboard/inline-editor/editors/ChromeEditors";
import {
  TestimonialsEditor, FaqEditor, PricingEditor, TeamEditor, GalleryEditor,
  TrustPointsEditor, WhyChooseUsEditor, ProcessStepsEditor, AnnouncementMessagesEditor,
} from "@/components/dashboard/inline-editor/editors/SectionCollectionEditors";
import {
  FormCopyEditor, ServiceAreasEditor, HeroStatsEditor, BusinessHoursEditor,
  AboutValuesEditor, AboutStatsEditor, CategoryTilesEditor,
  ReservationInfoEditor, RestaurantContactEditor,
} from "@/components/dashboard/inline-editor/editors/CoverageEditors";

/**
 * Iter 76 — contextual floating toolbar. Replaces the old permanent
 * 360px right panel. Rendered INSIDE the canvas frame (same coordinate
 * space as the selection outline) so it stays visually attached to the
 * selected element and moves/scrolls with it.
 *
 *  • Thin bar:   [element label] Content | Style | Advanced | ✕
 *  • Popover:    anchored below/beside the element; compact (≤380px) for
 *                text/image/button, wider (≤640px) for collection
 *                editors. Always temporary — closes on deselect, tab
 *                re-click, Done, or Escape.
 */

const COMPACT_TYPES = ["text", "image", "button"];
const BAR_H = 38;

export default function ContextualToolbar({ selection, outline, editorProps, onDeselect }) {
  const type = selection?.anchor?.type;
  const isCompact = COMPACT_TYPES.includes(type);
  const [activeTab, setActiveTab] = useState("content");
  const [open, setOpen] = useState(true);

  useEffect(() => { setActiveTab("content"); setOpen(true); }, [selection?.anchor?.testid]);

  if (!selection || !outline) return null;

  const frameWidth = outline.frameWidth || 800;
  const isTall = outline.height > 220;

  // ── Bar placement: above the element when there's room, otherwise
  //    inside its top edge (tall sections) or just below (small elements
  //    pinned to the top of the page).
  let barMode;
  if (outline.top >= BAR_H + 10) barMode = "above";
  else if (isTall) barMode = "inside";
  else barMode = "below";
  const barTop =
    barMode === "above" ? outline.top
    : barMode === "inside" ? outline.top + 8
    : outline.top + outline.height + 6;
  const barLeft = Math.max(8, Math.min(outline.left, Math.max(8, frameWidth - 372)));

  // ── Popover placement: keeps the selected element visible.
  const popWidth = Math.min(isCompact ? 380 : 640, frameWidth - 16);
  const popLeft = Math.max(8, Math.min(outline.left, frameWidth - popWidth - 8));
  let popTop;
  if (isTall) popTop = outline.top + (barMode === "inside" ? 8 + BAR_H + 8 : 10);
  else if (barMode === "below") popTop = barTop + BAR_H + 8;
  else popTop = outline.top + outline.height + 10;

  const supportsStyle = isCompact;
  const supportsAdvanced = isCompact;

  const onTabClick = (tab, enabled) => {
    if (!enabled) return;
    if (open && activeTab === tab) { setOpen(false); return; }
    setActiveTab(tab);
    setOpen(true);
  };

  return (
    <>
      <div
        data-inline-editor-ui="true"
        data-testid="inline-editor-context-toolbar"
        className="absolute z-40 flex items-center gap-1 rounded-lg bg-white border-2 border-black shadow-lg px-1.5 py-1"
        style={{
          top: barTop,
          left: barLeft,
          transform: barMode === "above" ? "translateY(calc(-100% - 6px))" : "none",
        }}
      >
        <span
          className="px-2 py-0.5 rounded-md bg-emerald-500 text-white text-[11px] font-black whitespace-nowrap max-w-[140px] truncate"
          data-testid="inline-editor-selection-label"
          title={typeLabel(type)}
        >
          {outline.label || selection.anchor.label}
        </span>
        <ToolbarTab label="Content"  active={open && activeTab === "content"}  onClick={() => onTabClick("content", true)}              testId="context-toolbar-tab-content" />
        <ToolbarTab label="Style"    active={open && activeTab === "style"}    disabled={!supportsStyle}    onClick={() => onTabClick("style", supportsStyle)}       testId="context-toolbar-tab-style" />
        <ToolbarTab label="Advanced" active={open && activeTab === "advanced"} disabled={!supportsAdvanced} onClick={() => onTabClick("advanced", supportsAdvanced)} testId="context-toolbar-tab-advanced" />
        <button
          onClick={onDeselect}
          className="w-6 h-6 rounded-md hover:bg-gray-100 flex items-center justify-center flex-shrink-0"
          title="Done with this element"
          data-testid="inline-editor-context-close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {open && (
        <div
          data-inline-editor-ui="true"
          data-testid="inline-editor-context-popover"
          className="absolute z-40 rounded-xl bg-white border-2 border-black shadow-2xl"
          style={{ top: popTop, left: popLeft, width: popWidth }}
        >
          <div className="max-h-[460px] overflow-y-auto p-4">
            <PopoverBody type={type} activeTab={activeTab} selection={selection} editorProps={editorProps} />
          </div>
        </div>
      )}
    </>
  );
}

function ToolbarTab({ label, active, disabled, onClick, testId }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className={`px-2.5 py-1 rounded-md text-[11px] font-black whitespace-nowrap ${
        active ? "bg-black text-white" : "text-gray-600 hover:bg-gray-100"
      } ${disabled ? "opacity-35 cursor-not-allowed hover:bg-transparent" : ""}`}
    >
      {label}
    </button>
  );
}

function PopoverBody({ type, activeTab, selection, editorProps }) {
  const {
    draft, publicPayload, updateField, bizId,
    onCanvasChange, saveWebsiteContentList,
  } = editorProps;

  if (activeTab === "style") {
    return <StyleTab selection={selection} draft={draft} publicPayload={publicPayload} updateField={updateField} />;
  }
  if (activeTab === "advanced") {
    return <AdvancedTab selection={selection} draft={draft} publicPayload={publicPayload} updateField={updateField} />;
  }

  switch (type) {
    case "text":     return <TextEditor selection={selection} draft={draft} publicPayload={publicPayload} updateField={updateField} />;
    case "image":    return <ImageEditor selection={selection} draft={draft} publicPayload={publicPayload} updateField={updateField} bizId={bizId} />;
    case "button":   return <ButtonEditor selection={selection} draft={draft} publicPayload={publicPayload} updateField={updateField} />;
    case "products": return <ProductsEditor bizId={bizId} onCanvasChange={onCanvasChange} />;
    case "menu":     return <MenuEditor publicPayload={publicPayload} updateField={updateField} saveAll={saveWebsiteContentList} onCanvasChange={onCanvasChange} />;
    case "services": return <ServicesEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} onCanvasChange={onCanvasChange} />;
    case "header":   return <HeaderEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} bizId={bizId} />;
    case "footer":   return <FooterEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "testimonials": return <TestimonialsEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "faqs":     return <FaqEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "pricing":  return <PricingEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "team":     return <TeamEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "gallery":  return <GalleryEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "trust_points":          return <TrustPointsEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "why_choose_us":         return <WhyChooseUsEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "process_steps":         return <ProcessStepsEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "announcement_messages": return <AnnouncementMessagesEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "form_copy": return <FormCopyEditor formKey={selection.anchor.formKey} draft={draft} publicPayload={publicPayload} updateField={updateField} />;
    case "service_areas": return <ServiceAreasEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "hero_stats": return <HeroStatsEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "business_hours": return <BusinessHoursEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "about_values": return <AboutValuesEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "about_stats": return <AboutStatsEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "category_tiles": return <CategoryTilesEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "reservation_info": return <ReservationInfoEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;
    case "restaurant_contact": return <RestaurantContactEditor publicPayload={publicPayload} saveAll={saveWebsiteContentList} />;;
    default:
      return <p className="text-xs text-gray-500">This element type isn&apos;t editable yet.</p>;
  }
}
